// =========================================================================
// ANIMORA MONETIZATION CATALOG
// Single source of truth for every paid product in the app (Premium,
// Custom Username, Chat Themes, Creator Subscriptions, Gifts). Analytics is FREE for everyone.
// Every product here is bought with coins (coins.spend() in app/page.tsx) -
// the price shown on a button always matches what is deducted, since both
// read this same file. Nothing is bought with money. Coins are earned free (achievements, streaks, snaps,
// referrals) or received as gifts - they can no longer be bought.
// =========================================================================

export type BillingPeriod = 'one_time' | 'month'

export type PlanCategory =
  | 'boost'
  | 'premium'
  | 'username'
  | 'chat_theme'
  | 'analytics'
  | 'subscription'
  | 'gift'

export type Plan = {
  id: string
  category: PlanCategory
  label: string
  amountRupees: number
  period: BillingPeriod
  durationDays?: number // for time-limited unlocks (premium, analytics, subscriptions)
  blurb?: string
  image?: string // gifts: artwork (public/gifts/*.webp, drawn on a black background)
}

// ---- Boost (REAL MONEY via Google Play Billing, one-time consumable products boost_6h / boost_24h / boost_3d) -----
// `amountRupees` is the price in rupees (INR). It must match the price set for the same product id in Play Console;
// the price the person actually pays and sees comes from Google Play (see BoostPlanPrice in app/page.tsx).
export const BOOST_PLANS: Plan[] = [
  { id: 'boost_6h', category: 'boost', label: '6 Hours', amountRupees: 20, period: 'one_time', durationDays: 0.25, blurb: 'Quick visibility bump' },
  { id: 'boost_24h', category: 'boost', label: '24 Hours', amountRupees: 50, period: 'one_time', durationDays: 1, blurb: 'Most popular' },
  { id: 'boost_3d', category: 'boost', label: '3 Days', amountRupees: 100, period: 'one_time', durationDays: 3, blurb: 'Best value for reach' },
]

// Boost anti-spam rules (checked on the SERVER in /api/wallet/transfer, shown in the Boost sheet).
// Anyone can boost - the paid price + these limits keep the Radar feed clean.
export const BOOST_LIMITS = {
  maxActive: 2,            // boosted posts/stories one person can have running at the same time
  maxPerDay: 3,            // boosts one person can start per rolling 24 hours
  cooldownMinutes: 10,     // minimum gap between two boosts by the same person
} as const

// (Website link on posts & reels is NOT a separate paid feature: it lives only in the Boost system - the Boost sheet's
// "Add a website link" - so there is no link price here.)

// ---- Creator milestone -------------------------------------------------------
// 1 million Allies AND 30 lakh reel views (TICK_MILESTONE_ALLIES / TICK_MILESTONE_VIEWS in
// lib/rewards-policy.ts, re-checked by the server in /api/rewards) = permanent free Green Tick AND
// every premium theme (chat wallpapers, home, header/composer). Nothing else: the
// sticker/guild limits still need an active Green Tick (GREEN_TICK_PRICE_COINS below).

// ---- Green Tick (was "Go Premium") ----------------------------------------
// One plan only: 3,000 coins for 30 days. While the tick is active the user gets the
// green tick next to their @username, every premium chat theme,
// (Green Tick does NOT remove ads - ads are shown to everyone),
// a higher custom-sticker limit and a higher guild member limit.
export const GREEN_TICK_PRICE_COINS = 3000
export const GREEN_TICK_DAYS = 30

export const PREMIUM_PLANS: Plan[] = [
  { id: 'premium_month', category: 'premium', label: 'Green Tick - 1 Month', amountRupees: GREEN_TICK_PRICE_COINS, period: 'month', durationDays: GREEN_TICK_DAYS, blurb: 'Renewing adds another 30 days on top' },
]

// Limits that change with the Green Tick. Free users get the first number,
// anyone with an active tick gets the second one.
export const FREE_STICKER_LIMIT = 20
export const PREMIUM_STICKER_LIMIT = 100
export const FREE_GUILD_MEMBER_LIMIT = 60
export const PREMIUM_GUILD_MEMBER_LIMIT = 100

// ---- Extra Guild Slots (stacks on top of the limit above) -----------------
// A guild/group's member cap is FREE_GUILD_MEMBER_LIMIT (or PREMIUM_GUILD_MEMBER_LIMIT
// with an active Green Tick) PLUS `groupMemberBonus * GROUP_MEMBER_BONUS_INCREMENT`.
// `groupMemberBonus` is a counter stored on the BUYER's own users/{uid} doc (not on
// the group), so it is a permanent account entitlement: it survives deleting a guild
// and creating a brand new one, and it applies to every guild that account owns.
// Bought with coins, one +50 block at a time - see selfGrant.groupMemberSlots in
// app/api/wallet/transfer/route.ts, which is the only thing allowed to increment it.
export const GROUP_MEMBER_BONUS_INCREMENT = 50
export const GROUP_MEMBER_BONUS_PRICE_COINS = 500
// Sanity ceiling so a compromised client can't request an absurd number of blocks in
// one call - the server checks against this too, not just the client-side button.
export const MAX_GROUP_MEMBER_BONUS_PER_PURCHASE = 1

// ---- Custom Username ------------------------------------------------------
// 1-4 characters: PREMIUM handles, bought ONCE with REAL MONEY through Google Play (one-time in-app products
//                 username_1 ... username_4, see lib/billing-products.ts). Coins can never buy them.
// 5+ characters:  normal handle - the first change is free, later changes cost STANDARD_USERNAME_CHANGE_COINS.
// PREMIUM_USERNAME_PRICES below is only a REFERENCE (rupees) - the price people really pay and see comes from Google Play.
export const USERNAME_MIN_LENGTH = 1 // shorter than this is refused everywhere
export const PREMIUM_USERNAME_MIN_LENGTH = 1
export const PREMIUM_USERNAME_MAX_LENGTH = 4
// Reference price in RUPEES, by number of characters (set the same numbers in Play Console).
export const PREMIUM_USERNAME_PRICES: Record<number, number> = {
  1: 1000,
  2: 500,
  3: 250,
  4: 100,
}
// Rename limit (any rename, free / coin / paid): after MAX_CHANGES renames the person cannot rename for LOCK_DAYS days.
export const USERNAME_CHANGE_LIMIT = { maxChanges: 5, lockDays: 15 }
export const STANDARD_USERNAME_CHANGE_COINS = 100

export const USERNAME_PLANS: Plan[] = [
  { id: 'username_custom_unlock', category: 'username', label: 'Reserve a custom handle', amountRupees: 100, period: 'one_time', blurb: 'One-time - pick any available username' },
  { id: 'username_change', category: 'username', label: 'Extra username change', amountRupees: STANDARD_USERNAME_CHANGE_COINS, period: 'one_time', blurb: 'After your free change is used' },
]

// ---- Premium Chat Themes --------------------------------------------------
// Bought straight from the chat's theme picker: tap a locked theme -> buy exactly that one.
export const CHAT_THEME_PRICE_COINS = 1500
export const CHAT_THEME_BUNDLE_COINS = 4000
export const CHAT_THEME_PLANS: Plan[] = [
  { id: 'theme_single', category: 'chat_theme', label: 'Single premium theme', amountRupees: CHAT_THEME_PRICE_COINS, period: 'one_time', blurb: 'Unlock one premium wallpaper' },
  { id: 'theme_bundle', category: 'chat_theme', label: 'All premium themes', amountRupees: CHAT_THEME_BUNDLE_COINS, period: 'one_time', blurb: 'Unlock every current & future theme' },
]

// ---- Creator Subscriptions (paid to a specific creator) --------------------
export const SUBSCRIPTION_PLANS: Plan[] = [
  { id: 'sub_supporter', category: 'subscription', label: 'Supporter', amountRupees: 200, period: 'month', durationDays: 30, blurb: 'Support this creator monthly' },
  { id: 'sub_vip', category: 'subscription', label: 'VIP', amountRupees: 500, period: 'month', durationDays: 30, blurb: 'Exclusive posts + priority replies' },
]

// ---- Digital Gifts / Tips ---------------------------------------------------
// Creator gifts. Prices are in coins (1 coin = Rs 1). Artwork lives in public/gifts/ and is drawn on
// a pure black background - always show it inside the dark .gift-card / .gift-tile boxes (globals.css),
// which blend the black away so the picture never shows a square edge.
export const GIFT_CATALOG: Plan[] = [
  { id: 'gift_rose', category: 'gift', label: 'Rose', amountRupees: 10, period: 'one_time', image: '/gifts/rose.webp', blurb: 'A simple way to show your love and support.' },
  { id: 'gift_sports_car', category: 'gift', label: 'Sports Car', amountRupees: 50, period: 'one_time', image: '/gifts/sports-car.webp', blurb: 'Show your excitement with a luxury ride!' },
  { id: 'gift_teddy', category: 'gift', label: 'Teddy Bear', amountRupees: 100, period: 'one_time', image: '/gifts/teddy-bear.webp', blurb: 'A warm gift to make the creator smile.' },
  { id: 'gift_crown', category: 'gift', label: 'Crown', amountRupees: 200, period: 'one_time', image: '/gifts/crown.webp', blurb: "Show you're a top fan and king/queen of support." },
  { id: 'gift_heart_wings', category: 'gift', label: 'Heart Wings', amountRupees: 500, period: 'one_time', image: '/gifts/heart-wings.webp', blurb: 'A special gift for someone special.' },
  { id: 'gift_fireworks', category: 'gift', label: 'Fireworks', amountRupees: 1000, period: 'one_time', image: '/gifts/fireworks.webp', blurb: 'Make their moment truly unforgettable!' },
]

// Old gifts were saved with emoji labels ("👑 Crown"). Given ANY stored gift label (new or old) this
// returns the artwork when we have it, otherwise the emoji that was in the label (or a gift box).
export const giftVisualFor = (label?: string): { image?: string; emoji: string; name: string } => {
  const raw = (label || '').trim()
  const name = raw.replace(/^[^A-Za-z0-9]+/, '').trim() // drop a leading emoji
  const hit = GIFT_CATALOG.find((g) => g.label.toLowerCase() === name.toLowerCase())
  if (hit) return { image: hit.image, emoji: '', name: hit.label }
  return { emoji: '', name: name || raw || 'Gift' }
}

export const ALL_PLANS: Plan[] = [
  ...BOOST_PLANS,
  ...PREMIUM_PLANS,
  ...USERNAME_PLANS,
  ...CHAT_THEME_PLANS,
  ...SUBSCRIPTION_PLANS,
  ...GIFT_CATALOG,
]

export const findPlan = (id: string): Plan | undefined => ALL_PLANS.find((p) => p.id === id)

// Premium-only chat wallpaper ids (must match CHAT_BACKGROUNDS ids in app/page.tsx)
// Every other wallpaper in CHAT_BACKGROUNDS is free.
export const PREMIUM_CHAT_THEME_IDS = [
  // classic
  'starfield-orbit', 'wave-flow',
  // black / dark premium
  'neon-skyline', 'aurora-night', 'galaxy-dust', 'crimson-waves', 'cyber-circuit', 'gold-noir',
  // white / light premium
  'lotus-mandala', 'pastel-aurora', 'marble-veil', 'botanical-line', 'paper-waves', 'terrazzo',
] as const
